import React, { useEffect, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Switch,
  Tooltip,
} from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  NotificationPreference,
  NotificationPreferencesState,
  UpdateNotificationPreference,
} from "../../../../shared/notifications/models/notificationModels";
import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from "../../../../shared/notifications/notificationPreferencesSlice";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { dt } from "../../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../../shared/hooks/useRedesignSetup";
import { CustomerSettingsState } from "../../models/models";
import { CustomerTypeCode } from "../../../shared/models/customerModels";
import { getCustomer } from "../../customerSettingsSlice";

// ---------------------------------------------------------------------------
// Event type → translation key (includes new WO events 40-46)
// ---------------------------------------------------------------------------
const eventTypeLabels: Record<number, string> = {
  1: "settings:notifications.eventTypes.newOfferReceived",
  2: "settings:notifications.eventTypes.offerViewed",
  3: "settings:notifications.eventTypes.offerAccepted",
  4: "settings:notifications.eventTypes.offerRejected",
  5: "settings:notifications.eventTypes.offerExpiringSoon",
  6: "settings:notifications.eventTypes.offerWithdrawn",
  7: "settings:notifications.eventTypes.providerConfirmationRequested",
  8: "settings:notifications.eventTypes.providerConfirmedService",
  9: "settings:notifications.eventTypes.confirmationReminder",
  10: "settings:notifications.eventTypes.confirmationOverdue",
  11: "settings:notifications.eventTypes.pastDeadlineWarning",
  20: "settings:notifications.eventTypes.workStarted",
  21: "settings:notifications.eventTypes.progressUpdatePosted",
  22: "settings:notifications.eventTypes.workCompleted",
  23: "settings:notifications.eventTypes.completionConfirmed",
  24: "settings:notifications.eventTypes.revisionRequested",
  25: "settings:notifications.eventTypes.reviewReceived",
  28: "settings:notifications.eventTypes.reviewReceived",
  300: "settings:notifications.eventTypes.partnerInvitationReceived",
  301: "settings:notifications.eventTypes.partnerInvitationAccepted",
  302: "settings:notifications.eventTypes.partnerInvitationDeclined",
  305: "settings:notifications.eventTypes.partnerInvitationReceived",
  307: "settings:notifications.eventTypes.privateRfsReceived",
  40: "settings:notifications.eventTypes.workOrderCreated",
  41: "settings:notifications.eventTypes.workOrderAssigned",
  42: "settings:notifications.eventTypes.workOrderStatusChanged",
  43: "settings:notifications.eventTypes.workOrderCompleted",
  44: "settings:notifications.eventTypes.workOrderOverdue",
  45: "settings:notifications.eventTypes.workOrderComment",
  46: "settings:notifications.eventTypes.recurringWorkOrderGenerated",
};

// ---------------------------------------------------------------------------
// Category definitions — each maps to a colored card group
// ---------------------------------------------------------------------------
type CategoryKey =
  | "serviceOffers"
  | "serviceConfirmation"
  | "serviceExecution"
  | "partnerships"
  | "operations";

interface CategoryDef {
  key: CategoryKey;
  titleKey: string;
  descKey: string;
  accent: string;
  accentSoft: string;
  icon: React.ReactNode;
  eventTypes: number[];
}

const categories: CategoryDef[] = [
  {
    key: "serviceOffers",
    titleKey: "settings:notifications.categories.serviceOffers",
    descKey: "settings:notifications.categories.serviceOffersDesc",
    accent: "#0C1117",
    accentSoft: "rgba(12, 17, 23, 0.06)",
    icon: <LocalOfferOutlinedIcon sx={{ fontSize: 16, color: "#0C1117" }} />,
    eventTypes: [1, 2, 3, 4, 5, 6],
  },
  {
    key: "serviceConfirmation",
    titleKey: "settings:notifications.categories.serviceConfirmation",
    descKey: "settings:notifications.categories.serviceConfirmationDesc",
    accent: "#E8A817",
    accentSoft: "rgba(232, 168, 23, 0.08)",
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#E8A817" }} />,
    eventTypes: [7, 8, 9, 10, 11],
  },
  {
    key: "serviceExecution",
    titleKey: "settings:notifications.categories.serviceExecution",
    descKey: "settings:notifications.categories.serviceExecutionDesc",
    accent: "#3B82F6",
    accentSoft: "rgba(59, 130, 246, 0.08)",
    icon: <BuildOutlinedIcon sx={{ fontSize: 16, color: "#3B82F6" }} />,
    eventTypes: [20, 21, 22, 23, 24, 25, 28],
  },
  {
    key: "partnerships",
    titleKey: "settings:notifications.categories.partnerships",
    descKey: "settings:notifications.categories.partnershipsDesc",
    accent: "#2D8C4E",
    accentSoft: "rgba(45, 140, 78, 0.08)",
    icon: <HandshakeOutlinedIcon sx={{ fontSize: 16, color: "#2D8C4E" }} />,
    eventTypes: [300, 301, 302, 305, 307],
  },
  {
    key: "operations",
    titleKey: "settings:notifications.categories.operations",
    descKey: "settings:notifications.categories.operationsDesc",
    accent: "#D4572B",
    accentSoft: "rgba(212, 87, 43, 0.08)",
    icon: <AssignmentOutlinedIcon sx={{ fontSize: 16, color: "#D4572B" }} />,
    eventTypes: [40, 41, 42, 43, 44, 45, 46],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const toUpdatePayload = (p: NotificationPreference): UpdateNotificationPreference => ({
  eventType: p.eventType,
  isInAppEnabled: p.isInAppEnabled,
  isEmailEnabled: p.isEmailEnabled,
});

// ---------------------------------------------------------------------------
// Switch styling — accent color per category
// ---------------------------------------------------------------------------
const switchSx = (accent: string) => ({
  "& .MuiSwitch-switchBase.Mui-checked": { color: accent },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: accent,
    opacity: 0.55,
  },
});

// ---------------------------------------------------------------------------
// Role pill — small badge showing user's view (owner / provider / both)
// ---------------------------------------------------------------------------
interface RolePillProps {
  customerType?: number;
  translate: (key: string) => string;
}

const RolePill: React.FC<RolePillProps> = ({ customerType, translate }) => {
  if (!customerType) return null;

  const isOwner =
    customerType === CustomerTypeCode.MachineOperator ||
    customerType === CustomerTypeCode.Both;
  const isProvider =
    customerType === CustomerTypeCode.ServiceProvider ||
    customerType === CustomerTypeCode.Both;

  const pills: React.ReactNode[] = [];
  if (isOwner) {
    pills.push(
      <Box
        key="owner"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          padding: "4px 10px",
          borderRadius: dt.radiusPill,
          backgroundColor: "rgba(212, 87, 43, 0.1)",
          color: "#A0421E",
          border: "1px solid rgba(212, 87, 43, 0.25)",
          fontSize: "11px",
          fontWeight: 600,
          fontFamily: dt.font,
          letterSpacing: "0.3px",
        }}
      >
        {translate("settings:notifications.roleMachineOwner")}
      </Box>
    );
  }
  if (isProvider) {
    pills.push(
      <Box
        key="provider"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          padding: "4px 10px",
          borderRadius: dt.radiusPill,
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          color: "#2563EB",
          border: "1px solid rgba(59, 130, 246, 0.25)",
          fontSize: "11px",
          fontWeight: 600,
          fontFamily: dt.font,
          letterSpacing: "0.3px",
        }}
      >
        {translate("settings:notifications.roleServiceProvider")}
      </Box>
    );
  }

  return <Box sx={{ display: "inline-flex", gap: "6px" }}>{pills}</Box>;
};

// ---------------------------------------------------------------------------
// Category Card
// ---------------------------------------------------------------------------
interface CategoryCardProps {
  def: CategoryDef;
  items: NotificationPreference[];
  index: number;
  translate: (key: string) => string;
  onTogglePref: (
    eventType: number,
    field: "isInAppEnabled" | "isEmailEnabled"
  ) => void;
  onToggleCategoryAll: (def: CategoryDef, items: NotificationPreference[]) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
  def,
  items,
  index,
  translate,
  onTogglePref,
  onToggleCategoryAll,
}) => {
  const anyInAppOn = items.some((i) => i.isInAppEnabled);

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: dt.cardBg,
        borderRadius: dt.radius,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        overflow: "hidden",
        animation: `fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${0.1 + index * 0.06}s both`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: "4px",
          backgroundColor: def.accent,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 1, md: 2 },
          padding: "14px 18px 14px 22px",
          backgroundColor: def.accentSoft,
          borderBottom: `1px solid ${dt.cardBorder}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: dt.radiusSm,
              backgroundColor: "#FFFFFF",
              border: `1px solid ${dt.cardBorder}`,
              flexShrink: 0,
            }}
          >
            {def.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: def.accent,
                fontFamily: dt.font,
                lineHeight: 1.2,
              }}
            >
              {translate(def.titleKey)}
            </Box>
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: 400,
                color: dt.textSecondary,
                fontFamily: dt.font,
                mt: "2px",
                lineHeight: 1.4,
              }}
            >
              {translate(def.descKey)}
            </Box>
          </Box>
        </Box>

        {/* Category-level "ALL" toggle */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,
            alignSelf: { xs: "flex-end", md: "center" },
          }}
        >
          <Box
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1.2px",
              color: dt.textSecondary,
              fontFamily: dt.font,
            }}
          >
            {translate("settings:notifications.allToggle")}
          </Box>
          <Switch
            checked={anyInAppOn}
            onChange={() => onToggleCategoryAll(def, items)}
            size="small"
            sx={switchSx(def.accent)}
          />
        </Box>
      </Box>

      {/* Column headers */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 56px 56px 56px", md: "1fr 80px 80px 80px" },
          gap: "8px",
          padding: { xs: "10px 14px 10px 22px", md: "10px 18px 10px 22px" },
          backgroundColor: dt.headerBg,
          borderBottom: `1px solid ${dt.rowBorder}`,
        }}
      >
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            color: "#8C9099",
            fontFamily: dt.font,
          }}
        >
          {translate("settings:notifications.eventType")}
        </Box>
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            color: "#8C9099",
            fontFamily: dt.font,
            textAlign: "center",
          }}
        >
          {translate("settings:notifications.inApp")}
        </Box>
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            color: "#8C9099",
            fontFamily: dt.font,
            textAlign: "center",
          }}
        >
          {translate("settings:notifications.email")}
        </Box>
        <Tooltip title={translate("settings:notifications.pushFootnote")} arrow>
          <Box
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1.2px",
              color: dt.textMuted,
              fontFamily: dt.font,
              textAlign: "center",
              cursor: "help",
            }}
          >
            {translate("settings:notifications.push")}*
          </Box>
        </Tooltip>
      </Box>

      {/* Event rows */}
      {items.map((pref, rowIdx) => (
        <Box
          key={pref.eventType}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 56px 56px 56px", md: "1fr 80px 80px 80px" },
            gap: "8px",
            padding: { xs: "8px 14px 8px 22px", md: "10px 18px 10px 22px" },
            alignItems: "center",
            borderBottom:
              rowIdx < items.length - 1 ? `1px solid ${dt.rowBorder}` : "none",
            transition: "background-color 0.15s ease",
            "&:hover": {
              backgroundColor: dt.rowHover,
            },
          }}
        >
          <Box
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: dt.textPrimary,
              fontFamily: dt.font,
              letterSpacing: "0.1px",
            }}
          >
            {translate(
              eventTypeLabels[pref.eventType] ||
                `settings:notifications.eventTypes.type${pref.eventType}`
            )}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Switch
              checked={pref.isInAppEnabled}
              onChange={() => onTogglePref(pref.eventType, "isInAppEnabled")}
              size="small"
              sx={switchSx(def.accent)}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Switch
              checked={pref.isEmailEnabled}
              onChange={() => onTogglePref(pref.eventType, "isEmailEnabled")}
              size="small"
              sx={switchSx(def.accent)}
            />
          </Box>
          <Tooltip title={translate("settings:notifications.pushFootnote")} arrow>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Switch checked={false} disabled size="small" />
            </Box>
          </Tooltip>
        </Box>
      ))}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const SettingsNotifications: React.FC = () => {
  useRedesignSetup();
  const { translate } = useTranslation("settings");
  const dispatch = useAppDispatch();
  const { preferences, loading } = useAppSelector<NotificationPreferencesState>(
    (state: any) => state.notificationPreferences
  );
  const { customer: ownCustomer } = useAppSelector<CustomerSettingsState>(
    (state: any) => state.customerSettings
  );

  useEffect(() => {
    dispatch(fetchNotificationPreferences());
    if (!ownCustomer) {
      dispatch(getCustomer());
    }
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group preferences by category, only including categories that have at least 1 pref
  const grouped = useMemo(() => {
    const catMap = new Map<CategoryKey, NotificationPreference[]>();
    for (const def of categories) {
      const items = preferences.filter((p) => def.eventTypes.includes(p.eventType));
      if (items.length > 0) {
        // preserve the category's event order
        const sorted = [...items].sort(
          (a, b) =>
            def.eventTypes.indexOf(a.eventType) - def.eventTypes.indexOf(b.eventType)
        );
        catMap.set(def.key, sorted);
      }
    }
    return catMap;
  }, [preferences]);

  const activeCategories = useMemo(
    () => categories.filter((c) => grouped.has(c.key)),
    [grouped]
  );

  // Worker heuristic: only operations events in the response
  const isWorkerOnly =
    activeCategories.length === 1 && activeCategories[0]?.key === "operations";

  const handleTogglePref = (
    eventType: number,
    field: "isInAppEnabled" | "isEmailEnabled"
  ) => {
    const updated: UpdateNotificationPreference[] = preferences.map((p) => {
      const base = toUpdatePayload(p);
      if (p.eventType !== eventType) return base;
      return { ...base, [field]: !p[field] };
    });
    dispatch(updateNotificationPreferences(updated));
  };

  const handleToggleCategoryAll = (
    def: CategoryDef,
    items: NotificationPreference[]
  ) => {
    const anyOn = items.some((i) => i.isInAppEnabled);
    const target = !anyOn; // if any are on → turn off; if all off → turn on
    const eventTypesInCat = new Set(items.map((i) => i.eventType));
    const updated: UpdateNotificationPreference[] = preferences.map((p) => {
      const base = toUpdatePayload(p);
      if (!eventTypesInCat.has(p.eventType)) return base;
      return { ...base, isInAppEnabled: target };
    });
    dispatch(updateNotificationPreferences(updated));
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (loading && preferences.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "96px 24px",
        }}
      >
        <CircularProgress size={28} sx={{ color: dt.blue }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
        maxWidth: "1080px",
        margin: "0 auto",
        padding: { xs: "16px 8px", md: "8px 0" },
      }}
    >
      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 1.5,
          mb: 2.5,
          animation: "headerReveal 0.5s cubic-bezier(0.4, 0, 0.2, 1) both",
        }}
      >
        <Box>
          <Box
            sx={{
              fontSize: { xs: "20px", md: "22px" },
              fontWeight: 800,
              color: dt.textPrimary,
              fontFamily: dt.font,
              letterSpacing: "-0.3px",
              lineHeight: 1.2,
            }}
          >
            {translate("settings:notifications.title")}
          </Box>
          <Box
            sx={{
              fontSize: "13px",
              fontWeight: 400,
              color: dt.textSecondary,
              fontFamily: dt.font,
              mt: 0.5,
              lineHeight: 1.5,
              maxWidth: "640px",
            }}
          >
            {translate("settings:notifications.description")}
          </Box>
        </Box>
        <RolePill customerType={ownCustomer?.customerType} translate={translate} />
      </Box>

      {/* Worker-only banner */}
      {isWorkerOnly && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            padding: "12px 16px",
            mb: 2,
            borderRadius: dt.radiusSm,
            backgroundColor: "rgba(59, 130, 246, 0.06)",
            border: "1px solid rgba(59, 130, 246, 0.2)",
            animation: "fadeUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.05s both",
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: "#3B82F6", mt: "1px" }} />
          <Box
            sx={{
              fontSize: "13px",
              fontWeight: 400,
              color: dt.textPrimary,
              fontFamily: dt.font,
              lineHeight: 1.5,
            }}
          >
            {translate("settings:notifications.workerBanner")}
          </Box>
        </Box>
      )}

      {/* Category cards */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {activeCategories.map((def, idx) => (
          <CategoryCard
            key={def.key}
            def={def}
            items={grouped.get(def.key)!}
            index={idx}
            translate={translate}
            onTogglePref={handleTogglePref}
            onToggleCategoryAll={handleToggleCategoryAll}
          />
        ))}
      </Box>

      {/* Footnote */}
      <Box
        sx={{
          fontSize: "11px",
          color: dt.textMuted,
          fontFamily: dt.font,
          mt: 2.5,
          fontStyle: "italic",
          textAlign: { xs: "center", md: "left" },
        }}
      >
        * {translate("settings:notifications.pushFootnote")}
      </Box>
    </Box>
  );
};

export default SettingsNotifications;
